package us.laantech.admin.menu.clients.ads

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import us.laantech.common.dto.company.CompanyBranch
import us.laantech.common.dto.company.PosAd
import us.laantech.common.api.KtorClient
import kotlin.time.Duration.Companion.seconds

class AdConfiguratorViewModel(companyBranch: CompanyBranch?): ViewModel() {

    private val _uiState = MutableStateFlow(AdConfiguratorUiState(companyBranch = companyBranch))
    val uiState = _uiState.asStateFlow()
    val ktClient = KtorClient.instance.client
    val s3Client = HttpClient() {

    }

    // Status polling: a single active loop, plus the set of jobIds we've already refreshed the
    // file list for so a job that stays COMPLETE across polls only triggers one refetch.
    private var pollJob: Job? = null
    private val refreshedJobIds = mutableSetOf<String>()

    fun fetchAdFiles() {
        viewModelScope.launch { refreshAdFiles() }
    }

    // returns list of presigned urls
    // mp4 files partial download for preview.
    // for thumbnails use http range request.
    private suspend fun refreshAdFiles() {
        val response = ktClient.get("/admin/pos/ads/list") {
            parameter("companyBranchUuid", _uiState.value.companyBranch?.uuid)
        }
        if (response.status.isSuccess()) {
            val adFileUrls = response.body<List<String>>()
            _uiState.update { it.copy(adFileUrls = adFileUrls) }
        }
    }

    // Fetches raw bytes off a presigned S3 URL. Uses the bare s3Client, NOT ktClient,
    // because ktClient attaches a Cognito Authorization header on every request, which
    // S3 rejects alongside the query-string presigned signature.
    suspend fun fetchBytes(url: String): ByteArray? =
        runCatching { s3Client.get(url).body<ByteArray>() }.getOrNull()

    fun deleteFile(fileName: String) {
        viewModelScope.launch {
            val response = ktClient.delete("/admin/pos/ads") {
                parameter("companyBranchUuid", _uiState.value.companyBranch?.uuid)
                parameter("fileName", fileName)
            }
            if (response.status.isSuccess()) {
                delay(3.seconds)
                fetchAdFiles()
            }
        }
    }

    fun fetchAdSequence() {
        viewModelScope.launch {
            val response = ktClient.get("/admin/pos/ads") {
                parameter("companyBranchUuid", _uiState.value.companyBranch?.uuid)
            }
            if (response.status.isSuccess()) {
                val ads: List<PosAd> = response.body()
                // The array index is the source of truth for ordering. Sort by the backend's
                // sequence (nulls last) so a saved order survives a save->reload round-trip even
                // if the rows come back unordered, then reindex so sequence == index from the start.
                val ordered = ads.sortedWith(compareBy(nullsLast(naturalOrder())) { it.sequence }).reindexed()
                _uiState.update { it.copy(adSequence = ordered) }
            }
        }
    }

    // Normalize each PosAd.sequence to match its position in the list. The array index is the
    // source of truth for ordering; call this whenever the list order or membership changes so
    // sequence never drifts stale.
    private fun List<PosAd>.reindexed(): List<PosAd> =
        mapIndexed { i, ad -> ad.copy(sequence = i.toLong()) }

    fun insertSegment(s3Url: String) {
        _uiState.update {
            val fileName = Url(s3Url).segments.lastOrNull().orEmpty()

            val ad = PosAd(
                sequence = 0,
                filename = fileName
            )

            it.copy(adSequence = (it.adSequence + ad).reindexed())

        }
    }

    fun removeSegment(posAd: PosAd) {
        _uiState.update {
            it.copy(adSequence = (it.adSequence - posAd).reindexed())
        }
    }

    // Update a single element of adSequence in place. Keyed by list index rather than value
    // equality: insertSegment always builds PosAd(sequence = 0, filename = ...), so the same file
    // inserted twice yields equals-equal elements; keying on index edits exactly the one card.
    fun updateSegment(index: Int, transform: (PosAd) -> PosAd) {
        _uiState.update { state ->
            state.copy(
                adSequence = state.adSequence.mapIndexed { i, posAd ->
                    if (i == index) transform(posAd) else posAd
                }
            )
        }
    }

    // Move one segment by offset positions (Up = -1, Down = +1). Keyed by index for the same
    // reason as updateSegment: duplicate files produce equals-equal PosAds. Out-of-range moves
    // (past either end) are a no-op.
    fun moveSegment(index: Int, offset: Int) {
        _uiState.update { state ->
            val target = index + offset
            if (index !in state.adSequence.indices || target !in state.adSequence.indices) {
                return@update state
            }
            state.copy(
                adSequence = state.adSequence.toMutableList().apply {
                    add(target, removeAt(index))
                }.reindexed()
            )
        }
    }

    // Entry point from the Upload button once the user has picked a file. Orchestrates the whole
    // flow: get a presigned URL -> PUT the bytes to S3 (indeterminate modal) -> close the modal ->
    // trigger media convert -> start polling status.
    fun uploadFile(fileName: String, bytes: ByteArray) {
        viewModelScope.launch {
            _uiState.update { it.copy(isUploading = true, uploadFileName = fileName, uploadError = null) }
            try {
                val url = getUploadUrl(fileName) ?: error("Could not get an upload URL")
                // Use the bare s3Client (no Cognito Authorization header), same rationale as
                // fetchBytes(): S3 rejects a bearer header alongside the presigned signature.
                // The Js engine can't report upload byte-progress, so the modal bar is indeterminate.
                val put = s3Client.put(url) { setBody(bytes) }
                if (!put.status.isSuccess()) error("Upload failed: ${put.status}")

                _uiState.update { it.copy(isUploading = false) }   // close the upload modal
                initMediaConvert(fileName)
                startPolling()
            } catch (e: Throwable) {
                _uiState.update { it.copy(isUploading = false, uploadError = e.message) }
            }
        }
    }

    // Returns a presigned S3 URL for uploading a file. The endpoint returns the URL as a plain
    // string; trim any surrounding quotes in case content-negotiation renders it JSON-quoted.
    private suspend fun getUploadUrl(fileName: String): String? {
        val response = ktClient.get("/admin/pos/ads/uploadUrl") {
            parameter("companyBranchUuid", _uiState.value.companyBranch?.uuid)
            parameter("fileName", fileName)
        }
        return if (response.status.isSuccess()) response.bodyAsText().trim().trim('"') else null
    }

    private fun initMediaConvert(fileName: String) {
        viewModelScope.launch {
            ktClient.put("/admin/pos/ads/process") {
                parameter("companyBranchUuid", _uiState.value.companyBranch?.uuid)
                parameter("fileName", fileName)
            }
        }
    }

    private suspend fun refreshMediaConvertStatus(): List<MediaConvertJobResponse> {
        val response = ktClient.get("/admin/pos/ads/process/status") {
            parameter("companyBranchUuid", _uiState.value.companyBranch?.uuid)
        }
        if (response.status == HttpStatusCode.OK) {
            val mediaConvertJobs = response.body<List<MediaConvertJobResponse>>()
            _uiState.update { it.copy(mediaConvertJobs = mediaConvertJobs) }
            return mediaConvertJobs
        }
        return _uiState.value.mediaConvertJobs
    }

    // Poll the status endpoint until no job is still in progress. Whenever a job newly reaches
    // COMPLETE, refresh the file list. ERROR/CANCELED are treated as terminal too so a failed job
    // can't loop forever.
    fun startPolling() {
        if (pollJob?.isActive == true) return   // don't stack loops if the user uploads again
        pollJob = viewModelScope.launch {
            delay(5.seconds)
            while (isActive) {
                val jobs = refreshMediaConvertStatus()

                // count (not any) so every newly-COMPLETE job is marked in one pass; any would
                // short-circuit on the first and re-refresh the rest on the next cycle.
                val newlyComplete = jobs.count {
                    it.status == "COMPLETE" && it.jobId != null && refreshedJobIds.add(it.jobId)
                }
                if (newlyComplete > 0) {
                    delay(8.seconds)
                    refreshAdFiles()
                }

                val stillRunning = jobs.any { it.status !in TERMINAL_STATUSES }
                if (jobs.isEmpty() || !stillRunning) break

                delay(POLL_INTERVAL_MS)
            }
        }
    }

    fun saveAdConfig() {
        viewModelScope.launch {
            _uiState.update { it.copy(isUploading = true) }
            val response = ktClient.put("/admin/pos/ads") {
                parameter("companyBranchUuid", _uiState.value.companyBranch?.uuid)
                setBody(_uiState.value.adSequence)
            }
            if (response.status.isSuccess()) {
                _uiState.update { it.copy(isUploading = false) }
            }
        }
    }

    companion object {
        private const val POLL_INTERVAL_MS = 10_000L
        private val TERMINAL_STATUSES = setOf("COMPLETE", "ERROR", "CANCELED")
    }

}